import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useRestaurant } from '@/hooks/useRestaurant';

interface MonthlyOrdersUsage {
  currentCount: number;
  monthName: string;
  startOfMonth: Date;
  endOfMonth: Date;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to track monthly orders usage for the current restaurant.
 * Counts all orders placed in the current calendar month across all branches.
 */
export function useMonthlyOrdersUsage(): MonthlyOrdersUsage {
  const { restaurant } = useRestaurant();
  const restaurantId = restaurant?.id;

  // Calculate current month boundaries
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const monthName = now.toLocaleString('default', { month: 'long', year: 'numeric' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['monthly-orders-usage', restaurantId, now.getMonth(), now.getFullYear()],
    queryFn: async () => {
      if (!restaurantId) return 0;

      // Get all branch IDs for this restaurant
      const { data: branches, error: branchError } = await supabase
        .from('branches')
        .select('id')
        .eq('restaurant_id', restaurantId);

      if (branchError) throw branchError;
      if (!branches || branches.length === 0) return 0;

      const branchIds = branches.map(b => b.id);

      // Count orders for this month across all branches
      const { count, error: orderError } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .in('branch_id', branchIds)
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());

      if (orderError) throw orderError;
      return count || 0;
    },
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - orders change frequently
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  return {
    currentCount: data ?? 0,
    monthName,
    startOfMonth,
    endOfMonth,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Hook to check if the restaurant is approaching or has reached the monthly order limit.
 * Returns warning status and remaining orders.
 */
export function useOrderLimitStatus() {
  const { currentCount, monthName, isLoading } = useMonthlyOrdersUsage();
  const { restaurant } = useRestaurant();

  // Get limit from subscription features (would need SubscriptionContext)
  // For now, this is a placeholder - the actual limit comes from FeatureGuard
  
  return {
    currentCount,
    monthName,
    isLoading,
    // These would be populated from subscription context:
    // limit: subscriptionFeatures?.monthly_orders_limit ?? Infinity,
    // remaining: limit - currentCount,
    // isNearLimit: (currentCount / limit) >= 0.8,
    // isAtLimit: currentCount >= limit,
  };
}
